
import React, { useEffect } from 'react';
import { XIcon } from './icons';
import { useTheme } from '../contexts/ThemeContext';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  const { colors } = useTheme();
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEsc);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[100] backdrop-blur-sm"
      onClick={onClose} // Close on overlay click
    >
      <div
        className="rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 relative transform transition-all duration-300 scale-95 animate-modalOpen"
        onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside modal
        style={{
          animation: 'modalOpenAnimation 0.3s ease-out forwards',
          backgroundColor: colors.background.paper,
          boxShadow: `0 20px 50px -10px ${colors.accent.mint}30`
        }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: colors.text.primary }}>{title}</h2>
          <button
            onClick={onClose}
            className="transition-all duration-300 hover:scale-110"
            style={{ color: colors.text.secondary }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.accent.mint; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.text.secondary; }}
            aria-label="Close modal"
          >
            <XIcon className="w-8 h-8" />
          </button>
        </div>
        <div className="text-neutral-light">
          {children}
        </div>
      </div>
      <style>{`
        @keyframes modalOpenAnimation {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-modalOpen {
          animation-name: modalOpenAnimation;
        }
      `}</style>
    </div>
  );
};

export default Modal;
    